/**
 * Auth Context
 * Provides the current session (user / admin) to the whole app and
 * handles login / signup / logout flows against the session-cookie API.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, login as apiLogin, signup as apiSignup, adminLogin as apiAdminLogin, logout as apiLogout } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getMe();
      setUser(data.user || null);
      setIsAdmin(!!data.isAdmin);
      return data;
    } catch (err) {
      setUser(null);
      setIsAdmin(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (tenant, token) => {
    const data = await apiLogin(tenant, token);
    setUser(data.user);
    setIsAdmin(false);
    return data.user;
  };

  const signup = async (userData) => {
    const data = await apiSignup(userData);
    setUser(data.user);
    setIsAdmin(false);
    return data.user;
  };

  const adminLogin = async (password) => {
    await apiAdminLogin(password);
    setIsAdmin(true);
    setUser(null);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      setIsAdmin(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, login, signup, adminLogin, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Read the last-used tenant cookie so the login form can be pre-filled
 * for returning users.
 */
export const getLastTenant = () => {
  const match = document.cookie.match(/(?:^|;\s*)last_tenant=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
};
